/**
 * Packstuecke und ihr Inhalt (KONZEPT.md §5.2, ADR 0012).
 *
 * Ein Packstueck ist Artikel **und** Lagerort. Wird es gebucht, bekommt jeder
 * Artikel darin eine eigene, abgeleitete Buchung -- so stimmt die Historie
 * jedes Einzelteils, ohne dass jemand die Kiste ausraeumt.
 *
 * Die Regel, vom Nutzer so festgelegt: **Was im Packstueck ist, bleibt mit ihm
 * verbunden.** Auch wenn das Packstueck auf einen anderen Lagerort gebucht
 * wird, in den Einsatz geht oder verliehen wird. Die abgeleitete Buchung zeigt
 * deshalb nie auf das Ziel des Packstuecks, sondern immer auf das Packstueck
 * selbst. Wo der Inhalt gerade ist, sagt dann der Weg ueber das Packstueck:
 * Der Leitungsroller liegt in GB-01, und GB-01 steht auf dem LKW.
 *
 * Rein und ohne Datenbank: Der Server leitet damit beim Eintreffen ab, das
 * Geraet zeigt damit offline sofort, was mitgegangen ist.
 */
/** Tiefste Schachtelung, die beim Ableiten verfolgt wird -- Kiste in Kiste in … */
export const MAX_PACKSTUECK_TIEFE = 10;
/** Mit der Kiste verloren, gestohlen, archiviert -- und nicht fuer sich allein. */
function mitDerKisteArchiviert(teil) {
    return teil.archiviert && teil.zuletzt_abgeleitet;
}
/**
 * Zieht diese Buchung des Packstuecks diesen Artikel darin mit?
 *
 * - **Bewegungen** ja -- alles, was darin steckt. Archiviertes nur, wenn es mit
 *   der Kiste verloren ging: Taucht die Kiste wieder auf und wird gebucht,
 *   kommt ihr Inhalt mit und gilt wieder als da (`nachBewegung` in
 *   `zustand.ts`). Ein Schraubendreher, der fuer sich allein verloren
 *   gemeldet war, bleibt verloren -- auch wenn er noch der Kiste zugeordnet ist.
 * - **Statusaenderungen** nicht: Dass eine Kiste defekt ist, heisst nicht, dass
 *   jeder Schraubendreher darin defekt ist. **Ausnahme** (ADR 0013 §10): ein
 *   archivierender Status. Eine verlorene Kiste ist samt Inhalt weg. Und wird
 *   ihr Status zurueckgenommen, kommt auch der Inhalt zurueck, der mit ihr
 *   verloren war (ADR 0016).
 * - **Korrekturen** nie: Sie berichtigen das eine Stueck, das gezaehlt oder
 *   gefunden wurde.
 */
export function ziehtInhaltMit(ausloeserin, teil, regeln) {
    switch (ausloeserin.art) {
        case "korrektur":
            return false;
        case "statusaenderung":
            if (ausloeserin.status === null)
                return false;
            return regeln.archivierend.has(ausloeserin.status)
                ? !teil.archiviert || mitDerKisteArchiviert(teil)
                : mitDerKisteArchiviert(teil);
        default:
            return !teil.archiviert || mitDerKisteArchiviert(teil);
    }
}
/**
 * Die abgeleitete Buchung fuer einen Artikel im Packstueck.
 *
 * Uebernimmt alles von der Ausloeserin -- Art, Zeitpunkt, Geraet, Benutzer,
 * Einsatzstelle --, nur Kennung, Artikel und Verweis sind eigene. Und das
 * Ziel einer Bewegung ist **der Lagerort des Packstuecks**: Bei einer
 * Umlagerung bleibt der Artikel damit, wo er ist, bei Einsatz und Ausgabe geht
 * er mit hinaus und bleibt dem Packstueck zugeordnet (`zustand.ts`). Kommt das
 * Packstueck zurueck, findet der Core ihn dort wieder, und er kommt mit.
 *
 * Eine Statusaenderung bleibt ohne Ziel. Den archivierenden Status bekommt der
 * Inhalt, wie er ist. Geht die Kiste auf einen anderen Status zurueck -- etwa
 * „defekt“ --, bekommt der Inhalt den **Standardstatus**, so wie er ihn auch
 * bekaeme, wenn die Kiste wieder auftauchte und bewegt wuerde.
 */
export function inhaltBuchung(ausloeserin, artikel_id, packstueck_lagerort_id, id, regeln) {
    if (ausloeserin.art === "statusaenderung") {
        const status = ausloeserin.status !== null && regeln.archivierend.has(ausloeserin.status)
            ? ausloeserin.status
            : regeln.standard;
        return { ...ausloeserin, id, artikel_id, status, abgeleitet_von: ausloeserin.id };
    }
    return {
        ...ausloeserin,
        id,
        artikel_id,
        ziel_lagerort_id: packstueck_lagerort_id,
        abgeleitet_von: ausloeserin.id,
    };
}
/**
 * Wie ein Artikel im Packstueck nach einer abgeleiteten Buchung dasteht -- fuer
 * das Geraet, das mehrere wartende Buchungen nacheinander vorwegnimmt.
 */
export function teilNach(abgeleitet, regeln) {
    const archiviert = abgeleitet.art === "statusaenderung" &&
        abgeleitet.status !== null &&
        regeln.archivierend.has(abgeleitet.status);
    return { archiviert, zuletzt_abgeleitet: true };
}
//# sourceMappingURL=packstueck.js.map